import { cx } from '@linaria/core';
import { useRef, useState } from 'react';
import { AccessCodes, getLabelText } from '@pm2/api';
import { shouldTerminateMegaMenu } from '@pm2/sitemap-utils';
import { Link } from '@provider/navigation';
import { useTrackingManager } from '@provider/tracking';
import { breakpoints } from '@ui/css';
import { useMatchBreakpoint, useLoaderState } from '@utils/react';
import { FavouriteButton } from './atom.favourite-button.js';
import { layerCls, loadingEllipsisCls, linkCls, buttonCls, vignetteWrapperCls, vignetteScaleContent, vignetteCls, labelCls, vignetteBackgroundCls, vignetteTextCls, vignetteTextWrapperCls } from './atom.layer.style.js';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { useVignette } from './hooks/useVignette.js';

const MAX_VIGNETTE_WIDTH = 360;
const MAX_MOBILE_VIGNETTE_WIDTH = 225;
let MegaMenuLayerDisplayTypes = /*#__PURE__*/function (MegaMenuLayerDisplayTypes) {
  MegaMenuLayerDisplayTypes["VIGNETTE"] = "vignette";
  MegaMenuLayerDisplayTypes["TEXT"] = "text";
  return MegaMenuLayerDisplayTypes;
}({});
const MegaMenuLayer = ({
  displayType,
  pageDescriptors,
  onNavigate
}) => {
  const isMobile = useMatchBreakpoint(breakpoints.mobile);
  const listRef = useRef(null);
  if (!pageDescriptors) {
    return null;
  }
  const maxVignetteWidth = isMobile ? MAX_MOBILE_VIGNETTE_WIDTH : MAX_VIGNETTE_WIDTH;
  return /*#__PURE__*/jsx("nav", {
    className: cx(layerCls, displayType === MegaMenuLayerDisplayTypes.VIGNETTE && "vignette", displayType === MegaMenuLayerDisplayTypes.TEXT && "text"),
    ref: listRef,
    children: pageDescriptors.filter(it => it.accessCode === AccessCodes.ALLOWED && !it.hidden).map(descriptor => {
      switch (displayType) {
        case MegaMenuLayerDisplayTypes.VIGNETTE:
          return /*#__PURE__*/jsx(VignetteLink, {
            descriptor: descriptor,
            onNavigate: onNavigate,
            maxVignetteWidth: maxVignetteWidth
          }, descriptor.id);
        case MegaMenuLayerDisplayTypes.TEXT:
        default:
          return /*#__PURE__*/jsx(TextLink, {
            descriptor: descriptor,
            onNavigate: onNavigate
          }, descriptor.id);
      }
    })
  });
};
const TextLink = ({
  descriptor,
  onNavigate
}) => {
  const tracker = useTrackingManager();
  const [isLoading, setLoading] = useState(false);
  const showThrobber = useLoaderState(isLoading);
  const onClick = async evt => {
    if (isLoading) {
      return;
    }
    setLoading(true);
    try {
      await onNavigate(evt, descriptor);
      tracker.trackEvent("userarea.navigation.toContent", {
        method: "megamenu",
        to: descriptor.path,
        targetType: descriptor.type ?? "Page"
      });
    } catch {
      // we only reset loading state in case a navigation error occurs during
      // navigation, since this component will be unmounted immediately after
      // navigation.
      setLoading(false);
    }
  };
  return shouldTerminateMegaMenu(descriptor) ? /*#__PURE__*/jsxs(Link, {
    href: descriptor.path,
    className: cx(linkCls, "text"),
    onClick: onClick,
    children: [descriptor.title, /*#__PURE__*/jsx("span", {
      className: cx(loadingEllipsisCls, showThrobber.current && "active"),
      children: "."
    })]
  }) : /*#__PURE__*/jsxs("button", {
    className: cx(buttonCls, linkCls, "text"),
    onClick: onClick,
    children: [descriptor.title, /*#__PURE__*/jsx("span", {
      className: cx(loadingEllipsisCls, showThrobber.current && "active"),
      children: "."
    })]
  });
};
const VignetteLink = ({
  descriptor,
  onNavigate,
  maxVignetteWidth
}) => {
  const tracker = useTrackingManager();
  const [isLoading, setLoading] = useState(false);
  const showThrobber = useLoaderState(isLoading);

  // In case the vignette isn't available, then fall back to simply rendering a
  // text link..
  if (!descriptor.vignette) {
    return /*#__PURE__*/jsx(TextLink, {
      descriptor: descriptor,
      onNavigate: onNavigate
    });
  }
  const onClick = async evt => {
    if (isLoading) {
      return;
    }
    setLoading(true);
    try {
      await onNavigate(evt, descriptor);
      tracker.trackEvent("userarea.navigation.toContent", {
        method: "megamenu",
        to: descriptor.path,
        targetType: descriptor.type
      });
    } catch {
      setLoading(false);
    }
  };
  return shouldTerminateMegaMenu(descriptor) ? /*#__PURE__*/jsx(Link, {
    href: descriptor.path,
    className: cx(linkCls, "vignette"),
    onClick: onClick,
    children: /*#__PURE__*/jsx(VignetteContent, {
      descriptor: descriptor,
      onNavigate: onNavigate,
      maxWidth: maxVignetteWidth,
      showThrobber: showThrobber.current
    })
  }) : /*#__PURE__*/jsx("button", {
    className: cx(buttonCls, linkCls, "vignette"),
    onClick: onClick,
    children: /*#__PURE__*/jsx(VignetteContent, {
      descriptor: descriptor,
      onNavigate: onNavigate,
      showThrobber: showThrobber.current,
      maxWidth: maxVignetteWidth
    })
  });
};

/**
 * Vignette content is moved to a separate function in order to allow for
 * rendering inside multiple wrappers (i.e.. <InternalLink> and <button>)
 */
const VignetteContent = ({
  descriptor,
  showThrobber,
  maxWidth
}) => {
  const vignette = descriptor.vignette;
  const ref = useRef(null);
  const textRef = useRef(null);
  const [isLoaded, disableFadeInTransition, hasOverflow] = useVignette({
    ref,
    textRef,
    vignetteData: vignette
  });

  // The vignette should be available here, as checked in the <VignetteLink />,
  // however, add this check to ensure the component won't fail if rendered in
  // other contexts.
  if (!vignette) {
    return null;
  }
  return /*#__PURE__*/jsxs(Fragment, {
    children: [/*#__PURE__*/jsx("div", {
      className: vignetteWrapperCls,
      style: {
        aspectRatio: vignette.width && vignette.height ? `${vignette.width} / ${vignette.height}` : isLoaded ? undefined : "16 / 9"
      },
      children: /*#__PURE__*/jsxs("div", {
        className: cx(vignetteBackgroundCls, isLoaded && "loaded", disableFadeInTransition && "disableFadeInTransition"),
        children: [/*#__PURE__*/jsxs("div", {
          className: vignetteScaleContent,
          children: [/*#__PURE__*/jsx("img", {
            ref: ref,
            className: vignetteCls,
            srcSet: `${vignette.url}?w=${maxWidth}, ${vignette.url}?w=${maxWidth * 1.5} 1.5x, ${vignette.url}?w=${maxWidth * 2} 2x`,
            src: `${vignette.url}?w=${maxWidth * 2}`,
            alt: descriptor.title,
            height: vignette.height,
            width: vignette.width
          }), descriptor.label && /*#__PURE__*/jsx("div", {
            className: labelCls,
            children: getLabelText(descriptor.label)
          })]
        }), /*#__PURE__*/jsx(FavouriteButton, {
          descriptor: descriptor
        })]
      })
    }), /*#__PURE__*/jsx("div", {
      className: cx(vignetteTextWrapperCls, hasOverflow && "hasOverflow"),
      "data-title": descriptor.title,
      children: /*#__PURE__*/jsxs("div", {
        className: vignetteTextCls,
        ref: textRef,
        children: [/*#__PURE__*/jsx("span", {
          children: descriptor.title
        }), /*#__PURE__*/jsx("span", {
          className: cx(loadingEllipsisCls, showThrobber && "active"),
          children: "."
        })]
      })
    })]
  });
};

export { MegaMenuLayer, MegaMenuLayerDisplayTypes };
//# sourceMappingURL=atom.layer.js.map
